import { Component, type ErrorInfo, type ReactNode } from 'react';
import { GlassButton, GlassCard, isStaleChunkError, reloadForStaleChunk } from '@servicesai/ui';

interface State {
  error: Error | null;
  /** A reload is under way; painting the card would flash it and then vanish. */
  reloading: boolean;
}

export class ConsoleErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, reloading: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A page whose chunk a deploy removed. The Reload button below is the whole
    // remedy, so press it once rather than asking a reviewer mid-case to.
    if (isStaleChunkError(error) && reloadForStaleChunk()) {
      this.setState({ reloading: true });
      return;
    }
    console.error('Console page failed to render', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.state.reloading) return null;
    return (
      <GlassCard className="mx-auto max-w-2xl p-6">
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">
            <h1 className="text-h4">
              {typeof document !== 'undefined' && document.documentElement.lang === 'ar'
                ? 'تعذر عرض مساحة العمل هذه'
                : 'This workspace could not be displayed'}
            </h1>
            <p className="mt-1 text-sm">
              {typeof document !== 'undefined' && document.documentElement.lang === 'ar'
                ? 'تم تسجيل الحادث محلياً. أعد تحميل الصفحة أو عد إلى قوائم المراجعة.'
                : 'The incident has been captured locally. Reload the page or return to the review queues.'}
            </p>
          </div>
        </div>
        <pre className="mt-4 max-h-40 overflow-auto rounded-lg bg-aeblack-900 p-3 text-xs text-white" dir="ltr">
          {this.state.error.message}
        </pre>
        <div className="mt-4 flex gap-2">
          <GlassButton variant="primary" onClick={() => window.location.reload()}>
            Reload
          </GlassButton>
          <GlassButton onClick={() => window.location.assign('/queues')}>Review queues</GlassButton>
        </div>
      </GlassCard>
    );
  }
}
